import {
    getZonedDateTimeParts,
    resolveTimeZone,
    zonedTimeToUtc,
} from '@/utils/timezone';

/**
 * 圖表資料格式化器
 * 專用於不同 chart 類型的資料轉換邏輯，獨立於配置層
 * 每個 formatter 將原始 API 資料轉換為 Chart.js 期望的格式
 */

// 統一的測試結果標籤配色
export const TEST_RESULT_LABELS = [
    'BAD CELL REPLACE',
    'RECHARGE & RETEST',
    'CAUTION',
    'BAD & REPLACE',
    'GOOD & RECHARGE',
    'GOOD & PASS',
];

const PALETTE = {
    red: '#D91E18',
    redLight: '#EB4D47',
    amber: '#FFBA66',
    yellow: '#FFD600',
    blue: '#4A90E2',
    blueLight: '#7CB7FF',
    green: '#2E8B57',
    greenLight: '#52B788',
    gray: '#607D8B',
    grayLight: '#B0BEC5',
};

export const TEST_RESULT_COLOR_MAP = {
    'BAD CELL REPLACE': PALETTE.red,
    'RECHARGE & RETEST': PALETTE.amber,
    CAUTION: PALETTE.yellow,
    'BAD & REPLACE': PALETTE.redLight,
    'GOOD & RECHARGE': PALETTE.greenLight,
    'GOOD & PASS': PALETTE.green,
};

const CATEGORY_COLORS = [
    PALETTE.red,
    PALETTE.redLight,
    PALETTE.amber,
    PALETTE.yellow,
    PALETTE.blue,
    PALETTE.blueLight,
    PALETTE.green,
    PALETTE.greenLight,
    PALETTE.gray,
    PALETTE.grayLight,
];

const hexToRgba = (hex, alpha = 1) => {
    const normalized = hex.replace('#', '');
    const value =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map(ch => ch + ch)
                  .join('')
            : normalized;
    const intVal = parseInt(value, 16);
    const r = (intVal >> 16) & 255;
    const g = (intVal >> 8) & 255;
    const b = intVal & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * 通用單系列建構器（按字母排序）
 * 適用於多數簡單的標籤-數值對應場景
 */
export const formatSingleSeries = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || '',
                data: values,
            },
        ],
    };
};

/**
 * 降序單系列建構器（按值由大到小排序）
 * 適用於 Top-N 排行場景
 */
export const formatSingleSeriesDescending = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(
        ([, va], [, vb]) => (Number(vb) || 0) - (Number(va) || 0)
    );
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || '',
                data: values,
            },
        ],
    };
};

/**
 * 時間序列月度格式化器
 * 適用於按月份展示趨勢的圖表
 */
export const formatTimeSeriesMonthly = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    // 按鍵（月份）排序，假設格式如 '2025-01', '2025-02' 等
    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Monthly',
                data: values,
                borderColor: PALETTE.blue,
                backgroundColor: hexToRgba(PALETTE.blue, 0.1),
                tension: 0.4,
                fill: false,
            },
        ],
    };
};

/**
 * 月度格式化器（僅取近 N 個月，預設 12 個月）
 * 適用於後端回傳較長時間跨度，但前端只需近 1 年的場景
 */
export const formatTimeSeriesMonthlyRecent = (row, limit = 12) => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length || !Number.isInteger(limit) || limit <= 0) {
        return { labels: [], datasets: [] };
    }

    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - (limit - 1), 1);

    const parsed = entries
        .map(([label, value]) => {
            const match = /^(\d{4})-(\d{2})$/.exec(label);
            if (!match) return null;
            const year = Number(match[1]);
            const monthIndex = Number(match[2]) - 1;
            if (monthIndex < 0 || monthIndex > 11) return null;
            const date = new Date(year, monthIndex, 1);
            return { label, value: Number(value) || 0, date };
        })
        .filter(Boolean)
        .filter(({ date }) => date >= start && date <= now)
        .sort((a, b) => a.date - b.date);

    if (!parsed.length) {
        return { labels: [], datasets: [] };
    }

    const labels = parsed.map(item => item.label);
    const values = parsed.map(item => item.value);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Monthly',
                data: values,
                borderColor: PALETTE.blue,
                backgroundColor: hexToRgba(PALETTE.blue, 0.1),
                tension: 0.4,
                fill: false,
            },
        ],
    };
};

/**
 * 時間序列小時格式化器
 * 適用於按小時展示的數據
 */
export const formatTimeSeriesHourly = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => {
        const aNum = Number(a);
        const bNum = Number(b);
        return aNum - bNum;
    });

    const labels = sorted.map(([hour]) => `${hour}:00`);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Hourly',
                data: values,
                backgroundColor: hexToRgba(PALETTE.blue, 0.7),
            },
        ],
    };
};

/**
 * 最近 24 小時、每 2 小時匯總的格式化器（使用指定時區的當日/昨日界線）
 * 根據當前時間回溯 24 小時，將資料按 2 小時一段聚合
 */
export const formatTimeSeriesHourlyRecent2h = (row, timeZone) => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const tz = resolveTimeZone(timeZone);
    const nowUtc = Date.now();

    // 今日 00:00 (tz) 的 UTC 時間戳
    const nowParts = getZonedDateTimeParts(nowUtc, tz);
    const todayStartUtcMs = zonedTimeToUtc(
        {
            year: nowParts.year,
            month: nowParts.month,
            day: nowParts.day,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        },
        tz
    );

    // 昨日 00:00 (tz) 的 UTC 時間戳
    const yesterdayProbe = todayStartUtcMs - 24 * 60 * 60 * 1000;
    const yesterdayParts = getZonedDateTimeParts(yesterdayProbe, tz);
    const yesterdayStartUtcMs = zonedTimeToUtc(
        {
            year: yesterdayParts.year,
            month: yesterdayParts.month,
            day: yesterdayParts.day,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        },
        tz
    );

    const twoHoursMs = 2 * 60 * 60 * 1000;

    const bins = [];
    for (
        let startUtcMs = yesterdayStartUtcMs;
        startUtcMs < todayStartUtcMs;
        startUtcMs = Math.min(startUtcMs + twoHoursMs, todayStartUtcMs)
    ) {
        const endUtcMs = Math.min(startUtcMs + twoHoursMs, todayStartUtcMs);
        bins.push({ startUtcMs, endUtcMs, value: 0 });
        if (endUtcMs === todayStartUtcMs) {
            break;
        }
    }

    const dayKeyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

    const addSample = (utcMs, value) => {
        if (utcMs < yesterdayStartUtcMs || utcMs >= todayStartUtcMs) {
            return;
        }
        const binIndex = bins.findIndex(
            bin => utcMs >= bin.startUtcMs && utcMs < bin.endUtcMs
        );
        if (binIndex >= 0) {
            bins[binIndex].value += value;
        }
    };

    entries.forEach(([dayKey, valueOrObj]) => {
        const dayMatch = dayKeyPattern.exec(dayKey);
        if (dayMatch && valueOrObj && typeof valueOrObj === 'object') {
            const year = Number(dayMatch[1]);
            const monthIndex = Number(dayMatch[2]) - 1;
            const day = Number(dayMatch[3]);

            Object.entries(valueOrObj).forEach(([hourKey, rawVal]) => {
                const hourNum = Number(hourKey);
                if (Number.isNaN(hourNum) || hourNum < 0 || hourNum > 23) {
                    return;
                }
                const utcMs = Date.UTC(year, monthIndex, day, hourNum, 0, 0, 0);
                if (Number.isNaN(utcMs)) return;
                addSample(utcMs, Number(rawVal) || 0);
            });
            return;
        }

        // 後備處理：原始扁平格式
        const parseUtcTimestamp = key => {
            if (key === null || key === undefined) return null;
            const str = String(key).trim();
            const reDateHour = /^(\d{4})[-/](\d{2})[-/](\d{2})[ T](\d{2})/;
            const match = reDateHour.exec(str);
            if (match) {
                const year = Number(match[1]);
                const monthIdx = Number(match[2]) - 1;
                const d = Number(match[3]);
                const h = Number(match[4]);
                const utcMs = Date.UTC(year, monthIdx, d, h, 0, 0, 0);
                if (!Number.isNaN(utcMs)) return utcMs;
            }
            const parsed = Date.parse(str);
            if (!Number.isNaN(parsed)) return parsed;
            return null;
        };

        const utcMs = parseUtcTimestamp(dayKey);
        if (utcMs !== null) {
            addSample(utcMs, Number(valueOrObj) || 0);
        }
    });

    const formatHourLabel = utcMs => {
        const { hour } = getZonedDateTimeParts(utcMs, tz);
        return String(hour).padStart(2, '0');
    };

    const labels = bins.map(
        bin =>
            `${formatHourLabel(bin.startUtcMs)}-${formatHourLabel(
                bin.endUtcMs
            )}`
    );
    const values = bins.map(bin => bin.value);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Last 24h (2h bins, local time)',
                data: values,
                backgroundColor: hexToRgba(PALETTE.blue, 0.7),
            },
        ],
    };
};

/**
 * 分佈式餅圖格式化器
 * 適用於顯示各種分類比例的圖表
 */
export const formatDistributionPie = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: CATEGORY_COLORS.slice(0, labels.length),
                hoverBackgroundColor: CATEGORY_COLORS.slice(0, labels.length),
            },
        ],
    };
};

/**
 * 固定標籤顏色的測試結果分佈圓餅圖
 */
export const formatResultDistributionPie = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    const colors = labels.map(
        (label, idx) =>
            TEST_RESULT_COLOR_MAP[label] ||
            CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
    );

    return {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: colors,
                hoverBackgroundColor: colors,
            },
        ],
    };
};

/**
 * 以月度資料生成測試結果堆疊長條，可調整粒度與範圍
 * 模式：'monthly' | 'quarterly' | 'yearly'
 * 範圍：數字（月或季）；yearly 則使用所有可用年份
 */
export const formatTestResultTimeline = (row, mode = 'monthly', range) => {
    const obj = row?.data || {};
    if (!obj || typeof obj !== 'object' || !Object.keys(obj).length) {
        return { labels: [], datasets: [] };
    }

    const entries = Object.entries(obj)
        .map(([key, val]) => {
            const m = /^(\d{4})-(\d{2})$/.exec(key);
            if (!m) return null;
            const year = Number(m[1]);
            const month = Number(m[2]);
            if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
            return { key, year, month, data: val };
        })
        .filter(Boolean)
        .sort((a, b) => a.key.localeCompare(b.key)); // 升序排序

    if (!entries.length) return { labels: [], datasets: [] };

    const clampRange = (val, min, max) => {
        if (typeof val !== 'number' || Number.isNaN(val)) return max;
        return Math.min(Math.max(val, min), max);
    };

    const collectLabelsAndBuckets = () => {
        if (mode === 'yearly') {
            const labels = [];
            const buckets = new Map(); // 年度 -> Map(結果 -> 加總值)
            entries.forEach(({ year, data }) => {
                if (!buckets.has(year)) buckets.set(year, new Map());
                const bucket = buckets.get(year);
                if (data && typeof data === 'object') {
                    Object.entries(data).forEach(([result, count]) => {
                        bucket.set(
                            result,
                            (bucket.get(result) || 0) + (Number(count) || 0)
                        );
                    });
                }
            });
            labels.push(...Array.from(buckets.keys()).sort());
            return { labels, buckets };
        }

        if (mode === 'quarterly') {
            const labelsRaw = entries.map(({ year, month }) => {
                const q = Math.floor((month - 1) / 3) + 1;
                return `${year}-Q${q}`;
            });
            const uniqueLabels = Array.from(new Set(labelsRaw)).sort();
            const effectiveRange = clampRange(range, 1, 8);
            const labels = uniqueLabels.slice(-effectiveRange);
            const buckets = new Map();
            labels.forEach(label => buckets.set(label, new Map()));
            entries.forEach(({ year, month, data }) => {
                const q = Math.floor((month - 1) / 3) + 1;
                const label = `${year}-Q${q}`;
                if (!buckets.has(label)) return;
                const bucket = buckets.get(label);
                if (data && typeof data === 'object') {
                    Object.entries(data).forEach(([result, count]) => {
                        bucket.set(
                            result,
                            (bucket.get(result) || 0) + (Number(count) || 0)
                        );
                    });
                }
            });
            return { labels, buckets };
        }

        // 月度模式
        const effectiveRange = clampRange(range, 6, 24);
        const labels = entries.map(e => e.key).slice(-effectiveRange);
        const buckets = new Map();
        labels.forEach(label => buckets.set(label, new Map()));
        entries.forEach(({ key, data }) => {
            if (!buckets.has(key)) return;
            const bucket = buckets.get(key);
            if (data && typeof data === 'object') {
                Object.entries(data).forEach(([result, count]) => {
                    bucket.set(
                        result,
                        (bucket.get(result) || 0) + (Number(count) || 0)
                    );
                });
            }
        });
        return { labels, buckets };
    };

    const { labels, buckets } = collectLabelsAndBuckets();
    if (!labels.length) return { labels: [], datasets: [] };

    const seenResults = new Set();
    buckets.forEach(bucket => {
        bucket.forEach((_, key) => seenResults.add(key));
    });
    // 使用既有的標籤順序，若有新標籤則依字母排序附加
    const orderedResults = [
        ...TEST_RESULT_LABELS.filter(label => seenResults.has(label)),
        ...Array.from(seenResults)
            .filter(label => !TEST_RESULT_LABELS.includes(label))
            .sort(),
    ];

    const datasets = orderedResults.map((resultLabel, idx) => {
        const color =
            TEST_RESULT_COLOR_MAP[resultLabel] ||
            CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
        return {
            label: resultLabel,
            data: labels.map(label => {
                const bucket = buckets.get(label);
                return bucket ? Number(bucket.get(resultLabel)) || 0 : 0;
            }),
            backgroundColor: color,
            borderColor: color,
            stack: 'results',
        };
    });

    const percentages = labels.map((label, labelIndex) => {
        const total = datasets.reduce(
            (sum, ds) =>
                sum +
                (Number(Array.isArray(ds.data) ? ds.data[labelIndex] : 0) || 0),
            0
        );
        return {
            label,
            total,
            results: orderedResults.map(resultLabel => {
                const ds = datasets.find(d => d.label === resultLabel);
                const value =
                    ds && Array.isArray(ds.data)
                        ? Number(ds.data[labelIndex]) || 0
                        : 0;
                const pct = total > 0 ? (value / total) * 100 : 0;
                return { result: resultLabel, value, pct };
            }),
        };
    });

    return { labels, datasets, percentages };
};

/**
 * 對比柱狀圖格式化器
 * 適用於兩個相關資料集的對比場景
 */
export const formatComparisonBar = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Count',
                data: values,
                backgroundColor: PALETTE.blue,
            },
        ],
    };
};

/**
 * 電池型別月度對比（當月 vs 上月）
 * data: { 'YYYY-MM': { 'AGM': 10, ... }, 'YYYY-MM': {...} }
 * 取最新兩個月份，產出兩個 dataset 深/淺色區分
 */
export const formatBatteryTypeMonthComparison = row => {
    const obj = row?.data || {};
    const months = Object.keys(obj)
        .filter(k => /^\d{4}-\d{2}$/.test(k))
        .sort();
    if (!months.length) return { labels: [], datasets: [] };

    const currentMonth = months[months.length - 1];
    const previousMonth = months.length >= 2 ? months[months.length - 2] : null;

    const currentData = obj[currentMonth] || {};
    const previousData = previousMonth ? obj[previousMonth] || {} : {};

    // 收集所有電池型別並保留出現順序（先上月後本月）
    const collectTypesInOrder = () => {
        const seen = new Set();
        const ordered = [];
        const pushKeys = obj => {
            Object.keys(obj || {}).forEach(k => {
                if (!seen.has(k)) {
                    seen.add(k);
                    ordered.push(k);
                }
            });
        };
        pushKeys(previousData);
        pushKeys(currentData);
        return ordered;
    };

    const types = collectTypesInOrder();

    const darkBlue = PALETTE.blue;
    const lightBlue = hexToRgba(PALETTE.blue, 0.45);

    const datasets = [];

    if (previousMonth) {
        const dataPrev = types.map(type => Number(previousData[type]) || 0);
        datasets.push({
            label: previousMonth,
            data: dataPrev,
            backgroundColor: types.map(() => darkBlue),
            borderColor: types.map(() => darkBlue),
            borderWidth: 1,
        });
    }

    const dataCurr = types.map(type => Number(currentData[type]) || 0);
    datasets.push({
        label: currentMonth,
        data: dataCurr,
        backgroundColor: types.map(() => lightBlue),
        borderColor: types.map(() => lightBlue),
        borderWidth: 1,
    });

    return { labels: types, datasets };
};

/**
 * 當季測試類型分佈（取當季月份加總後做甜甜圈/圓餅）
 * 以 calculate_date（或今天）決定季度，彙總該季度的月份資料
 */
export const formatTestTypeQuarterlyPie = row => {
    const data = row?.data || {};
    const calcDateStr = row?.config?.calculate_date;
    const refDate = calcDateStr ? new Date(calcDateStr) : new Date();
    const refYear = refDate.getFullYear();
    const refMonth = refDate.getMonth() + 1; // 1-12

    const quarter = Math.floor((refMonth - 1) / 3) + 1;
    const startMonth = (quarter - 1) * 3 + 1;
    const monthsInQuarter = [startMonth, startMonth + 1, startMonth + 2];

    // 收集年份僅 refYear（需求：取當季，同一年）
    const targetKeys = monthsInQuarter.map(
        m => `${refYear}-${String(m).padStart(2, '0')}`
    );

    const totals = new Map();

    targetKeys.forEach(key => {
        const monthData = data[key];
        if (!monthData || typeof monthData !== 'object') return;
        Object.entries(monthData).forEach(([testType, count]) => {
            totals.set(
                testType,
                (totals.get(testType) || 0) + (Number(count) || 0)
            );
        });
    });

    const labels = Array.from(totals.keys());
    const values = labels.map(label => totals.get(label) || 0);

    const colors = CATEGORY_COLORS;

    return {
        labels,
        datasets: [
            {
                data: values,
                backgroundColor: colors.slice(0, labels.length),
                hoverBackgroundColor: colors.slice(0, labels.length),
            },
        ],
    };
};

/**
 * 多資料集堆疊柱狀圖格式化器
 * 適用於多個相關指標的堆疊展示
 * 假設資料格式：{ '2025-01': { value1: 10, value2: 5 }, ... }
 */
export const formatStackedBar = row => {
    const obj = row?.data || {};
    if (!obj || typeof obj !== 'object' || !Object.keys(obj).length) {
        return { labels: [], datasets: [] };
    }

    const categories = Object.keys(obj).sort();
    const dataset1 = [];
    const dataset2 = [];

    categories.forEach(cat => {
        const catData = obj[cat];
        if (typeof catData === 'object') {
            dataset1.push(Number(catData.value1) || 0);
            dataset2.push(Number(catData.value2) || 0);
        } else {
            dataset1.push(0);
            dataset2.push(0);
        }
    });

    return {
        labels: categories,
        datasets: [
            {
                label: '',
                data: dataset1,
                backgroundColor: PALETTE.blue,
            },
            {
                label: '',
                data: dataset2,
                backgroundColor: PALETTE.greenLight,
            },
        ],
    };
};

/**
 * 年度趨勢折線圖格式化器
 * 適用於按年份展示長期趨勢
 */
export const formatAnnualTrend = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Annual',
                data: values,
                borderColor: PALETTE.greenLight,
                backgroundColor: hexToRgba(PALETTE.greenLight, 0.1),
                tension: 0.4,
                fill: false,
                pointRadius: 5,
                pointHoverRadius: 7,
            },
        ],
    };
};

/**
 * 版本分佈柱狀圖格式化器
 * 適用於韌體版本或應用版本分佈
 */
export const formatVersionDistribution = row => {
    const obj = row?.data || {};
    const entries = Object.entries(obj);
    if (!entries.length) {
        return { labels: [], datasets: [] };
    }

    // 版本號通常需要特殊排序（如 1.0 < 1.1 < 2.0）
    const sorted = entries.sort(([a], [b]) => {
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const aVal = aParts[i] || 0;
            const bVal = bParts[i] || 0;
            if (aVal !== bVal) return aVal - bVal;
        }
        return 0;
    });

    const labels = sorted.map(([label]) => label);
    const values = sorted.map(([, value]) => Number(value) || 0);

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Version Distribution',
                data: values,
                backgroundColor: PALETTE.blue,
            },
        ],
    };
};

/**
 * 版本分佈時間序列（按月 / 季 / 年）
 * 資料格式：{ '2024-01': { '1.0.0': 10, '1.1.0': 5 }, ... }
 * 期間粒度：'monthly' | 'quarterly' | 'yearly'
 * 版本作為資料集，時間作為 x 軸，適合堆疊柱狀圖
 */
export const formatVersionTimeline = (row, period = 'monthly') => {
    const obj = row?.data || {};
    if (!obj || typeof obj !== 'object' || !Object.keys(obj).length) {
        return { labels: [], datasets: [] };
    }

    const bucketMap = new Map(); // 區段 -> Map(版本 -> 加總值)
    const versionSet = new Set();

    const toBucket = key => {
        const str = String(key);
        const m1 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
        const m2 = /^(\d{4})-(\d{2})$/.exec(str);
        const m3 = /^(\d{4})$/.exec(str);

        const resolveMonth = (year, month) => {
            if (period === 'quarterly') {
                const q = Math.floor((month - 1) / 3) + 1;
                return `${year}-Q${q}`;
            }
            if (period === 'yearly') {
                return `${year}`;
            }
            return `${year}-${String(month).padStart(2, '0')}`;
        };

        if (m1) {
            const year = Number(m1[1]);
            const month = Number(m1[2]);
            if (Number.isFinite(year) && Number.isFinite(month)) {
                return resolveMonth(year, month);
            }
        }
        if (m2) {
            const year = Number(m2[1]);
            const month = Number(m2[2]);
            if (Number.isFinite(year) && Number.isFinite(month)) {
                return resolveMonth(year, month);
            }
        }
        if (m3) {
            const year = Number(m3[1]);
            if (Number.isFinite(year)) {
                return resolveMonth(year, 1);
            }
        }
        return null;
    };

    const addValue = (bucket, version, value) => {
        if (!bucket || !version) return;
        if (!bucketMap.has(bucket)) {
            bucketMap.set(bucket, new Map());
        }
        const vMap = bucketMap.get(bucket);
        const next = (vMap.get(version) || 0) + (Number(value) || 0);
        vMap.set(version, next);
        versionSet.add(version);
    };

    Object.entries(obj).forEach(([key, val]) => {
        const bucket = toBucket(key);
        if (!bucket) return;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            Object.entries(val).forEach(([version, count]) => {
                addValue(bucket, version, count);
            });
        }
    });

    if (!bucketMap.size || !versionSet.size) {
        return { labels: [], datasets: [] };
    }

    const labels = Array.from(bucketMap.keys()).sort();

    const compareVersion = (a, b) => {
        const aParts = String(a).split('.').map(Number);
        const bParts = String(b).split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i += 1) {
            const av = aParts[i] || 0;
            const bv = bParts[i] || 0;
            if (av !== bv) return av - bv;
        }
        return 0;
    };

    const versionsSorted = Array.from(versionSet).sort(compareVersion);

    const datasets = versionsSorted.map((version, idx) => {
        const data = labels.map(label => {
            const vMap = bucketMap.get(label);
            if (!vMap) return 0;
            return Number(vMap.get(version)) || 0;
        });
        const color = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
        return {
            label: version,
            data,
            backgroundColor: color,
        };
    });

    return {
        labels,
        datasets,
    };
};

/**
 * 版本分佈（時間窗篩選 + 月份分組）
 * 支援資料鍵為 YYYY-MM 或 YYYY-MM-DD，依指定時間窗過濾後按月堆疊
 * 時間窗：'30d' | 'quarter' | 'year'
 */
export const formatVersionTimelineWindow = (row, window = '30d') => {
    const obj = row?.data || {};
    if (!obj || typeof obj !== 'object' || !Object.keys(obj).length) {
        return { labels: [], datasets: [] };
    }

    const now = new Date();
    const start = (() => {
        const d = new Date(now);
        switch (window) {
            case 'quarter':
                d.setMonth(d.getMonth() - 3);
                return d;
            case 'year':
                d.setMonth(d.getMonth() - 12);
                return d;
            case '30d':
            default:
                d.setDate(d.getDate() - 30);
                return d;
        }
    })();

    const monthKey = date => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    };

    const bucketMap = new Map(); // 月份 -> Map(版本 -> 加總值)
    const versionSet = new Set();

    const addValue = (month, version, value) => {
        if (!month || !version) return;
        if (!bucketMap.has(month)) {
            bucketMap.set(month, new Map());
        }
        const vMap = bucketMap.get(month);
        vMap.set(version, (vMap.get(version) || 0) + (Number(value) || 0));
        versionSet.add(version);
    };

    Object.entries(obj).forEach(([k, v]) => {
        // k 可能是 YYYY-MM 或 YYYY-MM-DD
        const m1 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(k);
        const m2 = /^(\d{4})-(\d{2})$/.exec(k);
        let dt = null;
        if (m1) {
            dt = new Date(Number(m1[1]), Number(m1[2]) - 1, Number(m1[3]));
        } else if (m2) {
            dt = new Date(Number(m2[1]), Number(m2[2]) - 1, 1);
        }
        if (!dt || Number.isNaN(dt.getTime())) return;
        if (dt < start || dt > now) return;
        const bucket = monthKey(dt);

        if (v && typeof v === 'object' && !Array.isArray(v)) {
            Object.entries(v).forEach(([version, count]) => {
                addValue(bucket, version, count);
            });
        }
    });

    if (!bucketMap.size || !versionSet.size) {
        return { labels: [], datasets: [] };
    }

    const labels = Array.from(bucketMap.keys()).sort();
    const versions = Array.from(versionSet).sort((a, b) => {
        const aParts = String(a).split('.').map(Number);
        const bParts = String(b).split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i += 1) {
            const av = aParts[i] || 0;
            const bv = bParts[i] || 0;
            if (av !== bv) return av - bv;
        }
        return 0;
    });

    const datasets = versions.map((version, idx) => {
        const data = labels.map(label => {
            const vMap = bucketMap.get(label);
            return vMap ? Number(vMap.get(version)) || 0 : 0;
        });
        const color = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
        return {
            label: version,
            data,
            backgroundColor: color,
            stack: 'versions',
        };
    });

    return { labels, datasets };
};

/**
 * 版本分佈（按版本聚合，指定時間窗）
 * - 支援扁平格式：{ "v1": 10, "v2": 5 }（已經是選定時間窗）
 * - 支援月度 map：{ "2025-09": { "v1": 3 } }，會依時間窗 (30d/quarter/year) 篩選月份後加總
 */
export const formatVersionWindowDistribution = (row, window = '30d') => {
    const obj = row?.data || {};
    if (!obj || typeof obj !== 'object' || !Object.keys(obj).length) {
        return { labels: [], datasets: [] };
    }

    const now = new Date();
    const start = (() => {
        const d = new Date(now);
        switch (window) {
            case 'quarter':
                d.setMonth(d.getMonth() - 3);
                return d;
            case 'year':
                d.setMonth(d.getMonth() - 12);
                return d;
            case '30d':
            default:
                d.setDate(d.getDate() - 30);
                return d;
        }
    })();

    const versionTotals = new Map();

    const add = (version, value) => {
        if (!version) return;
        versionTotals.set(
            version,
            (versionTotals.get(version) || 0) + (Number(value) || 0)
        );
    };

    const entries = Object.entries(obj);
    const dateLike = entries.every(
        ([k, v]) =>
            typeof v === 'object' &&
            !Array.isArray(v) &&
            /^(\d{4})-(\d{2})(-\d{2})?$/.test(k)
    );

    if (dateLike) {
        entries.forEach(([k, v]) => {
            const m = /^(\d{4})-(\d{2})(-\d{2})?$/.exec(k);
            if (!m) return;
            const year = Number(m[1]);
            const month = Number(m[2]);
            const day = m[3] ? Number(m[3].slice(1)) : 1;
            const dt = new Date(year, month - 1, day);
            if (Number.isNaN(dt.getTime())) return;
            if (dt < start || dt > now) return;
            Object.entries(v || {}).forEach(([version, count]) =>
                add(version, count)
            );
        });
    } else {
        // 已事先依時間窗過濾的扁平資料：視鍵值為版本
        entries.forEach(([version, count]) => add(version, count));
    }

    const labels = Array.from(versionTotals.keys()).sort();
    const values = labels.map(v => versionTotals.get(v) || 0);

    const colors = CATEGORY_COLORS;

    return {
        labels,
        datasets: [
            {
                label: row?.code || 'Versions',
                data: values,
                backgroundColor: colors.slice(0, labels.length),
            },
        ],
    };
};
